export interface LanguageOption {
  code: string;
  labelKey: string;
  unknown?: true;
}

export const PINNED_LANGUAGE_CODES: readonly string[] = ['de', 'en'];

export function languageLabelKey(code: string): string {
  return `languages.${code}`;
}

export function sortLanguageOptions(
  codes: readonly string[],
  label: (code: string) => string,
  lang: string,
): LanguageOption[] {
  const toOption = (code: string): LanguageOption => ({
    code,
    labelKey: languageLabelKey(code),
  });
  const pinned = PINNED_LANGUAGE_CODES.filter((code) =>
    codes.includes(code),
  ).map(toOption);
  const rest = codes
    .filter((code) => !PINNED_LANGUAGE_CODES.includes(code))
    .map((code) => ({ option: toOption(code), name: label(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, lang))
    .map((entry) => entry.option);

  return [...pinned, ...rest];
}

/** Übersetzung `languages.<code>`, sonst Intl-Name in der Oberflächensprache, sonst das Kürzel. */
export function languageDisplayName(
  code: string,
  instant: (key: string) => string,
  lang: string,
): string {
  const key = languageLabelKey(code);
  const translated = instant(key);
  if (translated && translated !== key) return translated;

  try {
    const name = displayNamesFor(lang || 'de').of(code);
    if (name && name !== code) return name;
  } catch {
    // Kein gültiges Sprach-Tag, etwa ein Freitext-Altwert.
  }
  return code;
}

const displayNamesByLang = new Map<string, Intl.DisplayNames>();

function displayNamesFor(lang: string): Intl.DisplayNames {
  let displayNames = displayNamesByLang.get(lang);
  if (!displayNames) {
    displayNames = new Intl.DisplayNames([lang], {
      type: 'language',
      fallback: 'none',
    });
    displayNamesByLang.set(lang, displayNames);
  }
  return displayNames;
}
